// 个人档案私有接口（原 apps/customer/src/profiles/api.ts 的 Vue2/JS 版本）。
import {
  AuthError,
  ProfileError,
  httpTransport,
  headerValue,
  isUuid as uuid,
  isRecord as object,
  validateOrigin,
} from './http'

export const roles = ['male', 'female']
export const libraryRoles = [...roles, 'shared']
export const angles = ['front', 'left45', 'right45', 'left', 'right', 'smile']
export const angleLabels = {
  front: '正面',
  left45: '左侧面45度',
  right45: '右侧面45度',
  left: '左侧面',
  right: '右侧面',
  smile: '微笑',
}

export const emptyOptions = () => ({
  heightCm: null,
  weightKg: null,
  bodyType: null,
  acneRemoval: null,
  makeupEnhancement: null,
})

const messages = {
  NOT_CONFIGURED: '档案服务尚未配置。',
  INVALID_CONFIG: '档案服务地址配置无效。',
  INVALID_REQUEST: '提交内容不完整，请检查资料后重试。',
  PROFILE_IMAGE_FORMAT: '请选择真实的 JPG / PNG 图片；HEIC 或改后缀的文件请先导出为 JPG。',
  PROFILE_IMAGE_INVALID: '无法读取这张照片，请重新导出为 JPG 后再选择。',
  UNAUTHORIZED: '登录已失效，请重新登录。',
  FORBIDDEN: '当前身份无法访问个人档案。',
  PROFILE_DISABLED: '照片存储暂未开放，请稍后再试。',
  PROFILE_SERVICE_UNAVAILABLE: '档案服务暂不可用。写入结果不确定时，请先查询原操作。',
  PROFILE_STORAGE_LIMIT: '照片存储或历史记录已达技术上限，请联系负责人。',
  PROFILE_UPLOAD_TOO_LARGE: '照片处理后仍过大，请选择较小的原图。',
  PROFILE_UPLOAD_BUSY: '上传正在繁忙，请稍后再试。',
  PROFILE_PHOTO_NOT_FOUND: '照片不存在或访问已撤销，请重新读取档案。',
  PROFILE_OPERATION_NOT_FOUND: '尚未查到这次操作，可使用原编号重试。',
  PROFILE_OPERATION_CONFLICT: '操作编号对应不同内容，请重新读取并准备新操作。',
  REVISION_CONFLICT: '档案已被另一页面更新。输入已保留，请重新读取后核对。',
  PROFILE_REQUIRED: '请先保存档案，再上传角度照片。',
  PROFILE_CHANGED: '档案已删除或重建，请重新读取并核对。',
  PROFILE_ANGLE_OCCUPIED: '该角度已有照片，请先明确删除旧照片。',
  NETWORK: '网络连接失败。写入结果不确定时，请先查询原操作。',
  TIMEOUT: '请求超时。写入结果不确定时，请先查询原操作。',
  INVALID_RESPONSE: '档案服务响应异常，请重读或查询原操作。',
  CANCELLED: '已离开当前会话，此次结果不再显示。',
  UNSUPPORTED_PLATFORM: '小程序私有照片适配尚未开放，请使用网页版本。',
}

export function profileMessage(error) {
  const code = error instanceof ProfileError || error instanceof AuthError ? error.code : ''
  return Object.prototype.hasOwnProperty.call(messages, code)
    ? messages[code]
    : messages.PROFILE_SERVICE_UNAVAILABLE
}

export { uuid }

function invalid() {
  throw new ProfileError('INVALID_RESPONSE')
}

function exact(x, keys) {
  if (
    !object(x) ||
    Object.keys(x).length !== keys.length ||
    keys.some((k) => !Object.prototype.hasOwnProperty.call(x, k))
  )
    invalid()
}

const integer = (x, low, high) => Number.isSafeInteger(x) && x >= low && x <= high
const revision = (x) => integer(x, 0, 2147483647)
const utc = (x) =>
  typeof x === 'string' &&
  /^\d{4}-\d\d-\d\dT\d\d:\d\d:\d\d(?:\.\d{1,9})?Z$/.test(x) &&
  Number.isFinite(Date.parse(x))

export function parseOptions(x) {
  exact(x, ['heightCm', 'weightKg', 'bodyType', 'acneRemoval', 'makeupEnhancement'])
  if (
    (x.heightCm !== null && !integer(x.heightCm, 100, 250)) ||
    (x.weightKg !== null && !integer(x.weightKg, 30, 250)) ||
    ![null, 'slim', 'standard', 'full'].includes(x.bodyType) ||
    (x.acneRemoval !== null && typeof x.acneRemoval !== 'boolean') ||
    (x.makeupEnhancement !== null && typeof x.makeupEnhancement !== 'boolean')
  )
    invalid()
  return Object.assign({}, x)
}

export function parseLibraryPhoto(x) {
  exact(x, ['id', 'role', 'width', 'height', 'createdAt'])
  if (!uuid(x.id) || !libraryRoles.includes(x.role) || !integer(x.width, 1, 4096) || !integer(x.height, 1, 4096) || x.width * x.height > 12000000 || !utc(x.createdAt)) invalid()
  return { ...x }
}

export function parseProfile(x) {
  exact(x, ['role', 'id', 'revision', 'options', 'photos'])
  if (
    !roles.includes(x.role) ||
    (x.id !== null && !uuid(x.id)) ||
    !revision(x.revision) ||
    !Array.isArray(x.photos) ||
    x.photos.length > 6
  )
    invalid()
  const options = parseOptions(x.options)
  const photos = x.photos.map((p) => {
    exact(p, ['id', 'angle', 'status', 'contentType', 'bytes', 'width', 'height', 'sha256', 'createdAt'])
    if (
      !uuid(p.id) ||
      !angles.includes(p.angle) ||
      p.status !== 'READY' ||
      p.contentType !== 'image/jpeg' ||
      !integer(p.bytes, 1, 8388608) ||
      !integer(p.width, 1, 4096) ||
      !integer(p.height, 1, 4096) ||
      p.width * p.height > 12000000 ||
      typeof p.sha256 !== 'string' ||
      !/^[0-9a-f]{64}$/.test(p.sha256) ||
      !utc(p.createdAt)
    )
      invalid()
    return Object.assign({}, p)
  })
  if (
    new Set(photos.map((p) => p.angle)).size !== photos.length ||
    new Set(photos.map((p) => p.id)).size !== photos.length ||
    photos.some((p, i) => i > 0 && angles.indexOf(p.angle) <= angles.indexOf(photos[i - 1].angle)) ||
    (x.id === null && (photos.length > 0 || Object.values(options).some((v) => v !== null))) ||
    (x.id !== null && x.revision === 0)
  )
    invalid()
  return { role: x.role, id: x.id, revision: x.revision, options, photos }
}

export function parseProfiles(x) {
  exact(x, ['items'])
  if (!Array.isArray(x.items) || x.items.length !== 2) invalid()
  const result = x.items.map(parseProfile)
  if (result[0].role !== 'male' || result[1].role !== 'female') invalid()
  return result
}

export function parseProfileCapabilities(x) {
  exact(x, [
    'enabled',
    'provider',
    'maxBytes',
    'maxWidth',
    'maxHeight',
    'maxPixels',
    'acceptedTypes',
    'consentVersion',
    'modelingEnabled',
  ])
  if (
    typeof x.enabled !== 'boolean' ||
    !(x.enabled ? ['local-dev', 'private-local'] : ['disabled']).includes(x.provider) ||
    x.maxBytes !== 5242880 ||
    x.maxWidth !== 4096 ||
    x.maxHeight !== 4096 ||
    x.maxPixels !== 12000000 ||
    !Array.isArray(x.acceptedTypes) ||
    x.acceptedTypes.join('|') !== 'image/jpeg|image/png' ||
    x.consentVersion !== 'photo-generation-v1' ||
    x.modelingEnabled !== false
  )
    invalid()
  return Object.assign({}, x)
}

export function parseReceipt(x) {
  exact(x, ['operationId', 'role', 'revision', 'profileId', 'photoId', 'action'])
  if (
    !uuid(x.operationId) ||
    !roles.includes(x.role) ||
    !integer(x.revision, 1, 2147483647) ||
    !uuid(x.profileId) ||
    !['SAVE', 'UPLOAD', 'DELETE_PHOTO', 'DELETE_PROFILE'].includes(x.action) ||
    (['UPLOAD', 'DELETE_PHOTO'].includes(x.action) ? !uuid(x.photoId) : x.photoId !== null)
  )
    invalid()
  return Object.assign({}, x)
}

export function createProfilesApi(base, development = false, transport = httpTransport) {
  async function request(token, path, data, binary = false) {
    const origin = validateOrigin(base, development)
    let response
    try {
      response = await transport(
        Object.assign(
          {
            url: origin + '/v1/profiles' + path,
            method: data === undefined ? 'GET' : 'POST',
            data,
            headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
          },
          binary ? { responseType: 'arraybuffer' } : {},
        ),
      )
    } catch (error) {
      throw error instanceof AuthError || error instanceof ProfileError
        ? error
        : new ProfileError('NETWORK')
    }
    if (response.status !== 200) {
      const allowed = {
        400: ['INVALID_REQUEST'],
        401: ['UNAUTHORIZED'],
        403: ['FORBIDDEN'],
        404: ['PROFILE_PHOTO_NOT_FOUND', 'PROFILE_OPERATION_NOT_FOUND'],
        409: [
          'REVISION_CONFLICT',
          'PROFILE_REQUIRED',
          'PROFILE_CHANGED',
          'PROFILE_ANGLE_OCCUPIED',
          'PROFILE_OPERATION_CONFLICT',
        ],
        413: ['PROFILE_UPLOAD_TOO_LARGE'],
        429: ['PROFILE_UPLOAD_BUSY'],
        503: ['PROFILE_DISABLED', 'PROFILE_STORAGE_LIMIT', 'PROFILE_SERVICE_UNAVAILABLE'],
      }
      const code =
        response.status === 401
          ? 'UNAUTHORIZED'
          : response.status === 403
            ? 'FORBIDDEN'
            : object(response.data) &&
                typeof response.data.code === 'string' &&
                (allowed[response.status] || []).includes(response.data.code)
              ? response.data.code
              : binary && response.status === 404
                ? 'PROFILE_PHOTO_NOT_FOUND'
                : 'PROFILE_SERVICE_UNAVAILABLE'
      throw new ProfileError(code, response.status)
    }
    if (binary) {
      const type = headerValue(response.headers, 'content-type')
      if (
        type !== 'image/jpeg' ||
        !(response.data instanceof ArrayBuffer) ||
        response.data.byteLength < 4 ||
        response.data.byteLength > 8388608
      )
        invalid()
      const bytes = new Uint8Array(response.data)
      if (
        bytes[0] !== 255 ||
        bytes[1] !== 216 ||
        bytes[bytes.length - 2] !== 255 ||
        bytes[bytes.length - 1] !== 217
      )
        invalid()
    }
    return response.data
  }

  function check(role, input) {
    if (!roles.includes(role) || !uuid(input.operationId) || !revision(input.expectedRevision))
      throw new ProfileError('INVALID_REQUEST')
    if ('profileId' in input && !uuid(input.profileId)) throw new ProfileError('INVALID_REQUEST')
    if (
      'consentConfirmed' in input &&
      (input.consentConfirmed !== true || input.consentVersion !== 'photo-generation-v1')
    )
      throw new ProfileError('INVALID_REQUEST')
  }

  async function write(token, role, suffix, input, action) {
    check(role, input)
    const receipt = parseReceipt(await request(token, '/' + role + suffix, input))
    if (receipt.operationId !== input.operationId || receipt.role !== role || receipt.action !== action)
      invalid()
    return receipt
  }

  function checkId(id) {
    if (!uuid(id)) throw new ProfileError('INVALID_REQUEST')
  }

  return {
    library: async token => {
      const rows = await request(token, '/library')
      if (!Array.isArray(rows) || rows.length > 100) invalid()
      const photos = rows.map(parseLibraryPhoto)
      if (new Set(photos.map(p => p.id)).size !== photos.length) invalid()
      return photos
    },
    uploadLibrary: async (token, role, input) => {
      if (!libraryRoles.includes(role) || !uuid(input.operationId) || !['image/jpeg', 'image/png'].includes(input.contentType) || !input.base64 || input.consentConfirmed !== true || input.consentVersion !== 'photo-generation-v1') throw new ProfileError('INVALID_REQUEST')
      const photo = parseLibraryPhoto(await request(token, '/library/' + role, input))
      if (photo.role !== role) invalid()
      return photo
    },
    libraryContent: async (token, id) => { checkId(id); return request(token, '/library/' + id + '/thumbnail', undefined, true) },
    deleteLibrary: async (token, id) => { checkId(id); const result = await request(token, '/library/' + id + '/delete', {}); exact(result, ['deleted']); if (result.deleted !== true) invalid() },
    capabilities: async (token) => parseProfileCapabilities(await request(token, '/capabilities')),
    list: async (token) => parseProfiles(await request(token, '')),
    save: (token, role, input) => {
      parseOptions(input.options)
      return write(token, role, '/save', input, 'SAVE')
    },
    upload: (token, role, input) => {
      if (!angles.includes(input.angle) || !['image/jpeg', 'image/png'].includes(input.contentType) || !input.base64)
        throw new ProfileError('INVALID_REQUEST')
      return write(token, role, '/photos', input, 'UPLOAD')
    },
    deletePhoto: (token, role, id, input) => {
      checkId(id)
      return write(token, role, '/photos/' + id + '/delete', input, 'DELETE_PHOTO')
    },
    deleteProfile: (token, role, input) => write(token, role, '/delete', input, 'DELETE_PROFILE'),
    operation: async (token, id) => {
      checkId(id)
      const result = parseReceipt(await request(token, '/operations/' + id))
      if (result.operationId !== id) invalid()
      return result
    },
    content: async (token, id) => {
      checkId(id)
      return request(token, '/photos/' + id + '/content', undefined, true)
    },
  }
}
